import { Contact } from '../types';

/**
 * Calculates a dynamic health score and status based on the time since the last interaction.
 * Mimics the exact logic defined in SCHEMA.sql (calculate_health_score function).
 *
 * Score decay pattern:
 * - Within 1x frequency: 80-100 (On Track)
 * - 1x to 2x frequency: 40-80 (Needs Attention)
 * - > 2x frequency: 0-40 (Overdue)
 */
export function calculateHealthScore(
  lastInteractionDate: string | undefined,
  contactFrequencyDays: number | undefined,
): { score: number; status: Contact['status']; overdueDays: number } {
  // Default fallback if no target frequency is set (e.g. 30 days)
  const targetDays = contactFrequencyDays || 30;

  if (!lastInteractionDate) {
    // No interaction history -> show N/A state (score -1 signals "not yet scored")
    return { score: -1, status: 'new' as Contact['status'], overdueDays: 0 };
  }

  // Detect date-only values: bare "YYYY-MM-DD" or midnight-UTC timestamps.
  // Parse the date portion as local to avoid timezone shift.
  const dateOnly = lastInteractionDate.match(/^(\d{4})-(\d{2})-(\d{2})(T00:00:00(.000)?Z?)?$/);
  let lastInteraction: Date;
  if (dateOnly) {
    lastInteraction = new Date(+dateOnly[1], +dateOnly[2] - 1, +dateOnly[3]);
  } else {
    lastInteraction = new Date(lastInteractionDate);
  }
  const now = new Date();

  // Compare local calendar dates to avoid timezone drift
  const msPerDay = 1000 * 60 * 60 * 24;
  const todayLocal = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const lastLocal = new Date(
    lastInteraction.getFullYear(),
    lastInteraction.getMonth(),
    lastInteraction.getDate(),
  );

  const daysElapsed = Math.round((todayLocal.getTime() - lastLocal.getTime()) / msPerDay);

  // Prevent negative elapsed days if date is somehow in the future
  const daysSince = Math.max(0, daysElapsed);

  let score: number;

  if (daysSince <= targetDays) {
    // Zone 1: on track, score 80-100
    score = Math.round(100 - (daysSince / targetDays) * 20);
  } else if (daysSince <= targetDays * 2) {
    // Zone 2: needs attention, score 40-80
    score = Math.round(80 - ((daysSince - targetDays) / targetDays) * 40);
  } else {
    // Zone 3: overdue, score 0-40
    score = Math.round(Math.max(0, 40 - ((daysSince - targetDays * 2) / targetDays) * 40));
  }

  // Determine status and overdue days
  let status: Contact['status'] = 'on_track';
  let overdueDays = 0;

  if (daysSince > targetDays * 2) {
    status = 'overdue';
    overdueDays = daysSince - targetDays;
  } else if (daysSince > targetDays) {
    status = 'needs_attention';
    overdueDays = daysSince - targetDays;
  }

  return { score, status, overdueDays };
}

/**
 * Recalculates all contact health scores based on current date.
 */
export function recalculateAllContacts(contacts: Contact[]): Contact[] {
  return contacts.map((contact) => {
    const { score, status, overdueDays } = calculateHealthScore(
      contact.lastInteractionDate,
      contact.contactFrequencyDays,
    );
    return {
      ...contact,
      healthScore: score,
      status,
      overdueDays,
    };
  });
}
